'use client';

import {
   type ComponentProps,
   type KeyboardEvent,
   type ReactNode,
   type RefObject,
   use,
   createContext,
   useCallback,
   useEffect,
   useId,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { createPortal } from 'react-dom';
import { invariant } from '@ifixit/helpers';
import { cn } from '@ifixit/ui/lib/utils';
import { useControllableState } from '../hooks/use-controllable-state';
import { useStableCallback } from '../hooks/use-stable-callback';
import { afterExit } from '../lib/after-exit';
import { type PopupPlacement, followAnchor, placePopup } from '../lib/place-popup';
import { FaIcon } from './icon';

/**
 * A select-only combobox (WAI-ARIA APG). Focus never leaves the trigger: the
 * open list is driven through aria-activedescendant, so nothing has to trap,
 * move or restore focus. The list renders in place and is lifted into the top
 * layer with the Popover API, which keeps it inside whatever dialog, sheet or
 * form owns the trigger while escaping their clipping and transforms.
 */

type OpenIntent = 'selected' | 'first' | 'last' | 'search';

interface SelectContextValue {
   value: string | undefined;
   open: boolean;
   disabled: boolean;
   required: boolean;
   triggerId: string;
   listId: string;
   valueNode: HTMLElement | null;
   setValueNode: (node: HTMLElement | null) => void;
   setOpen: (open: boolean) => void;
   setValue: (value: string) => void;
}

/**
 * What event handlers and effects share but rendering never reads. The
 * highlight lives here and in the DOM rather than in state: moving it touches
 * two elements instead of re-rendering every option on each arrow key or hover.
 */
interface SelectInternals {
   trigger: HTMLButtonElement | null;
   list: HTMLDivElement | null;
   active: HTMLElement | null;
   openIntent: OpenIntent;
   search: string;
   searchTimer: number;
}

const SelectContext = createContext<SelectContextValue | null>(null);
const InternalsContext = createContext<RefObject<SelectInternals> | null>(null);
// True for parts rendered inside the open list. While the list is closed the
// parts render no DOM; the selected item still mirrors itself into the trigger.
const ListContext = createContext(false);
const GroupLabelContext = createContext<string | undefined>(undefined);

interface SelectProps {
   value?: string;
   defaultValue?: string;
   // Method syntax keeps callers free to take a narrower string union.
   onValueChange?(value: string): void;
   open?: boolean;
   defaultOpen?: boolean;
   onOpenChange?: (open: boolean) => void;
   disabled?: boolean;
   required?: boolean;
   name?: string;
   form?: string;
   children?: ReactNode;
}

function Select({
   value: valueProp,
   defaultValue,
   onValueChange,
   open: openProp,
   defaultOpen = false,
   onOpenChange,
   disabled = false,
   required = false,
   name,
   form,
   children,
}: SelectProps) {
   // The hook's own onChange only fires in controlled mode; the setters
   // below notify uncontrolled consumers too.
   const [value, setValueState] = useControllableState({
      prop: valueProp,
      defaultProp: defaultValue,
   });
   const [openState, setOpenState] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
   });
   const open = openState && !disabled;
   const [valueNode, setValueNode] = useState<HTMLElement | null>(null);

   const triggerId = useId();
   const listId = useId();
   const internalsRef = useRef<SelectInternals>({
      trigger: null,
      list: null,
      active: null,
      openIntent: 'selected',
      search: '',
      searchTimer: 0,
   });
   const nativeSelectRef = useRef<HTMLSelectElement>(null);

   const setOpen = useStableCallback((next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
   });

   const setValue = useStableCallback((next: string) => {
      if (next === value) return;
      setValueState(next);
      onValueChange?.(next);
   });

   // React sets the native select without a change event; forms that watch
   // for edits (dirty tracking, auto submit) wait for one.
   const reportedValueRef = useRef(value);
   useEffect(() => {
      if (reportedValueRef.current === value) return;
      reportedValueRef.current = value;
      nativeSelectRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
   }, [value]);

   // Carpenter builds without the React Compiler; unmemoized, every part of
   // every Select re-renders whenever its owner does.
   const context = useMemo(
      () => ({
         value,
         open,
         disabled,
         required,
         triggerId,
         listId,
         valueNode,
         setValueNode,
         setOpen,
         setValue,
      }),
      [
         value,
         open,
         disabled,
         required,
         triggerId,
         listId,
         valueNode,
         setValueNode,
         setOpen,
         setValue,
      ]
   );

   return (
      <SelectContext value={context}>
         <InternalsContext value={internalsRef}>{children}</InternalsContext>
         {name != null && (
            // The form's side of the control: it submits the value, enforces
            // `required`, and lets CSS and forms see a real checked option.
            <select
               ref={nativeSelectRef}
               aria-hidden
               tabIndex={-1}
               name={name}
               form={form}
               required={required}
               disabled={disabled}
               value={value ?? ''}
               onChange={event => setValue(event.target.value)}
               className="pointer-events-none sr-only"
            >
               <option value={value ?? ''} />
            </select>
         )}
      </SelectContext>
   );
}

function SelectGroup({ children, ...props }: ComponentProps<'div'>) {
   const inList = use(ListContext);
   const labelId = useId();
   if (!inList) return children;
   return (
      <GroupLabelContext value={labelId}>
         <div role="group" aria-labelledby={labelId} data-slot="select-group" {...props}>
            {children}
         </div>
      </GroupLabelContext>
   );
}

function SelectValue({
   placeholder,
   className,
   ...props
}: Omit<ComponentProps<'span'>, 'children'> & { placeholder?: ReactNode }) {
   const { value, setValueNode } = useSelect();
   return (
      // The selected item portals its content in here. React sends a click on
      // portaled content to the item rather than the trigger, so the span
      // stays out of hit testing and every press lands on the trigger.
      <span
         ref={setValueNode}
         data-slot="select-value"
         className={cn('pointer-events-none', className)}
         {...props}
      >
         {isEmpty(value) ? placeholder : null}
      </span>
   );
}

function SelectTrigger({
   className,
   size = 'default',
   children,
   id,
   disabled,
   onClick,
   onMouseDown,
   onKeyDown,
   onKeyUp,
   onBlur,
   ...props
}: Omit<ComponentProps<'button'>, 'ref'> & {
   size?: 'sm' | 'default';
}) {
   const select = useSelect();
   const internalsRef = useInternals();
   // Stable, so a re-render never detaches the node an effect cleanup needs.
   const setTriggerNode = useCallback(
      (node: HTMLButtonElement | null) => {
         internalsRef.current.trigger = node;
      },
      [internalsRef]
   );

   function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
      onKeyDown?.(event);
      if (event.defaultPrevented || event.ctrlKey || event.metaKey) return;
      const internals = internalsRef.current;
      // Space picks the highlighted option, unless it is part of a word
      // being typed.
      const typing =
         event.key.length === 1 && !event.altKey && !(event.key === ' ' && internals.search === '');
      if (typing) pushSearch(internals, event.key);

      const handled = select.open
         ? handleOpenKey(event, typing)
         : handleClosedKey(event.key, typing);
      if (handled) event.preventDefault();
   }

   function handleClosedKey(key: string, typing: boolean) {
      if (typing) openWith('search');
      else if (key === 'Home') openWith('first');
      else if (key === 'End') openWith('last');
      else if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(key)) openWith('selected');
      else return false;
      return true;
   }

   function handleOpenKey(event: KeyboardEvent, typing: boolean) {
      const internals = internalsRef.current;
      if (typing) {
         applySearch(internals);
         return true;
      }
      switch (event.key) {
         case 'ArrowDown': {
            moveActive(internals, 1);
            return true;
         }
         case 'ArrowUp': {
            if (event.altKey) commitActive();
            else moveActive(internals, -1);
            return true;
         }
         case 'PageDown': {
            moveActive(internals, PAGE_SIZE);
            return true;
         }
         case 'PageUp': {
            moveActive(internals, -PAGE_SIZE);
            return true;
         }
         case 'Home': {
            moveActive(internals, -Infinity);
            return true;
         }
         case 'End': {
            moveActive(internals, Infinity);
            return true;
         }
         case 'Enter':
         case ' ': {
            commitActive();
            return true;
         }
         default: {
            return false;
         }
      }
   }

   function openWith(intent: OpenIntent) {
      internalsRef.current.openIntent = intent;
      select.setOpen(true);
   }

   function commitActive() {
      const { active } = internalsRef.current;
      if (active?.isConnected) active.click();
      else select.setOpen(false);
   }

   return (
      <button
         ref={setTriggerNode}
         id={id ?? select.triggerId}
         type="button"
         role="combobox"
         aria-haspopup="listbox"
         aria-expanded={select.open}
         aria-controls={select.open ? select.listId : undefined}
         aria-required={select.required || undefined}
         disabled={select.disabled || disabled}
         data-slot="select-trigger"
         data-size={size}
         data-state={select.open ? 'open' : 'closed'}
         data-placeholder={isEmpty(select.value) ? '' : undefined}
         className={cn(
            "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive flex w-fit touch-manipulation items-center justify-between gap-2 rounded-lg border bg-card active:bg-muted px-3 py-2 text-sm whitespace-nowrap transition-[color,background-color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            className
         )}
         onMouseDown={event => {
            onMouseDown?.(event);
            // Safari takes focus off a button on press, which would close the
            // open list through the blur and let the click reopen it. Focus
            // is taken on click instead.
            event.preventDefault();
         }}
         onClick={event => {
            onClick?.(event);
            if (event.defaultPrevented) return;
            event.currentTarget.focus({ preventScroll: true });
            if (select.open) select.setOpen(false);
            else openWith('selected');
         }}
         onKeyDown={handleKeyDown}
         onKeyUp={event => {
            onKeyUp?.(event);
            // Firefox clicks a button on Space keyup even when the keydown
            // was handled, which would toggle the list straight back.
            if (event.key === ' ') event.preventDefault();
         }}
         onBlur={event => {
            onBlur?.(event);
            const { list } = internalsRef.current;
            if (select.open && !list?.contains(event.relatedTarget)) select.setOpen(false);
         }}
         {...props}
      >
         {children}
         <FaIcon name="caret-down" className="size-4 opacity-50" />
      </button>
   );
}

function SelectContent({
   className,
   children,
   align = 'start',
   side = 'bottom',
   sideOffset = 4,
   onMouseDown,
   onClick,
   onFocus,
   ...props
}: ComponentProps<'div'> & {
   align?: 'start' | 'center' | 'end';
   side?: 'top' | 'bottom';
   sideOffset?: number;
}) {
   const { open, setOpen, listId } = useSelect();
   const internalsRef = useInternals();
   const [mounted, setMounted] = useState(open);
   if (open && !mounted) setMounted(true);

   const setListNode = useCallback(
      (node: HTMLDivElement | null) => {
         internalsRef.current.list = node;
      },
      [internalsRef]
   );

   // A layout effect, not useStableCallback: that one updates after paint, so
   // the first placement would still see the previous render's props.
   const placementRef = useRef<PopupPlacement>({
      align,
      side,
      sideOffset,
      matchAnchorWidth: true,
   });
   useLayoutEffect(() => {
      placementRef.current = { align, side, sideOffset, matchAnchorWidth: true };
   });

   useLayoutEffect(() => {
      const internals = internalsRef.current;
      const { list, trigger } = internals;
      if (!open || !list || !trigger) return;

      function reposition() {
         if (!list || !trigger) return;
         const visible = placePopup(list, trigger, placementRef.current);
         if (!visible) setOpen(false);
      }
      function handlePointerDown(event: PointerEvent) {
         const target = event.target;
         if (!(target instanceof Node)) return;
         if (list?.contains(target) || trigger?.contains(target)) return;
         setOpen(false);
      }
      // Dialogs, sheets and the task composer listen for Escape on the
      // document; the window sees it first, so the key closes only the list.
      function handleKeyDown(event: globalThis.KeyboardEvent) {
         if (event.key !== 'Escape') return;
         event.preventDefault();
         event.stopPropagation();
         setOpen(false);
      }

      nameListAfter(list, trigger);
      list.showPopover?.();
      reposition();
      highlightInitial(internals);

      const stopFollowing = followAnchor(list, reposition);
      // Options can change under an open list; a highlight on one that left
      // would leave aria-activedescendant naming nothing.
      const listChanges = new MutationObserver(() => {
         if (!internals.active || internals.active.isConnected) return;
         internals.openIntent = 'selected';
         highlightInitial(internals);
      });
      listChanges.observe(list, { childList: true, subtree: true });
      window.addEventListener('keydown', handleKeyDown, { capture: true });
      document.addEventListener('pointerdown', handlePointerDown, { capture: true });
      return () => {
         stopFollowing();
         listChanges.disconnect();
         window.removeEventListener('keydown', handleKeyDown, { capture: true });
         document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
         setActive(internals, null);
         internals.search = '';
         list.hidePopover?.();
      };
   }, [open, setOpen, internalsRef]);

   useLayoutEffect(() => {
      if (open || !mounted) return;
      return afterExit(internalsRef.current.list, () => setMounted(false));
   }, [open, mounted, internalsRef]);

   if (!mounted) return children;
   return (
      <ListContext value={true}>
         <div
            ref={setListNode}
            id={listId}
            role="listbox"
            // "manual": the component owns dismissal, the browser only
            // supplies the top layer.
            popover="manual"
            data-slot="select-content"
            data-state={open ? 'open' : 'closed'}
            className={cn(
               'bg-popover text-popover-foreground fixed inset-auto z-50 m-0 outline-none min-w-[8rem] overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg border p-1 shadow-md',
               'scale-95 opacity-0 transition-[opacity,scale,display,overlay] transition-discrete duration-100 ease-out motion-reduce:transition-none',
               'data-[side=bottom]:origin-top data-[side=top]:origin-bottom data-[state=closed]:pointer-events-none',
               'data-[state=open]:scale-100 data-[state=open]:opacity-100 data-[state=open]:duration-150 starting:data-[state=open]:scale-95 starting:data-[state=open]:opacity-0',
               className
            )}
            tabIndex={-1}
            onMouseDown={event => {
               onMouseDown?.(event);
               // Pressing an option must not pull focus off the trigger.
               // Chromium also reports a press on the list's own scrollbar
               // here, and preventing that one would kill the drag; it moves
               // focus to the list instead, which hands it straight back.
               const onScrollbar =
                  event.target === event.currentTarget &&
                  event.nativeEvent.offsetX >= event.currentTarget.clientWidth;
               if (!onScrollbar) event.preventDefault();
            }}
            onFocus={event => {
               onFocus?.(event);
               internalsRef.current.trigger?.focus({ preventScroll: true });
            }}
            onClick={event => {
               onClick?.(event);
               // Inside a wrapping <label> the click would go on to activate
               // the trigger and reopen the list that just closed.
               event.preventDefault();
            }}
            {...props}
         >
            {children}
         </div>
      </ListContext>
   );
}

function SelectLabel({ className, ...props }: ComponentProps<'div'>) {
   const inList = use(ListContext);
   const id = use(GroupLabelContext);
   if (!inList) return null;
   return (
      <div
         id={id}
         data-slot="select-label"
         className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
         {...props}
      />
   );
}

function SelectItem({
   className,
   children,
   value,
   disabled = false,
   textValue,
   onClick,
   onPointerMove,
   ...props
}: Omit<ComponentProps<'div'>, 'value'> & {
   value: string;
   disabled?: boolean;
   /** What typeahead matches, when the content is not plain text. */
   textValue?: string;
}) {
   const select = useSelect();
   const internalsRef = useInternals();
   const inList = use(ListContext);
   const id = useId();
   const selected = select.value === value;
   const mirrored = selected && select.valueNode ? createPortal(children, select.valueNode) : null;
   if (!inList) return mirrored;

   return (
      <>
         <div
            id={id}
            role="option"
            aria-selected={selected}
            aria-disabled={disabled || undefined}
            data-slot="select-item"
            data-value={value}
            data-text={textValue}
            data-state={selected ? 'checked' : 'unchecked'}
            data-disabled={disabled ? '' : undefined}
            className={cn(
               "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground active:bg-accent [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none pointer-coarse:py-2.5 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:min-w-0 *:[span]:last:items-center *:[span]:last:gap-2",
               className
            )}
            onClick={event => {
               onClick?.(event);
               if (event.defaultPrevented || disabled) return;
               select.setValue(value);
               select.setOpen(false);
            }}
            onPointerMove={event => {
               onPointerMove?.(event);
               // A finger scrolling the list should not drag the highlight.
               if (disabled || event.pointerType === 'touch') return;
               const internals = internalsRef.current;
               if (internals.active !== event.currentTarget) {
                  setActive(internals, event.currentTarget, { scroll: false });
               }
            }}
            {...props}
         >
            <span
               aria-hidden
               className="absolute right-2 flex size-3.5 items-center justify-center"
            >
               {selected && <FaIcon name="check" className="size-4" />}
            </span>
            <span>{children}</span>
         </div>
         {mirrored}
      </>
   );
}

function SelectSeparator({ className, ...props }: ComponentProps<'div'>) {
   const inList = use(ListContext);
   if (!inList) return null;
   return (
      <div
         aria-hidden
         data-slot="select-separator"
         className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
         {...props}
      />
   );
}

const PAGE_SIZE = 10;
const SEARCH_RESET_MS = 700;

function useSelect() {
   const context = use(SelectContext);
   invariant(context, 'Select parts must be rendered inside a Select');
   return context;
}

function useInternals() {
   const internalsRef = use(InternalsContext);
   invariant(internalsRef, 'Select parts must be rendered inside a Select');
   return internalsRef;
}

function isEmpty(value: string | undefined) {
   return value === undefined || value === '';
}

function enabledOptions(list: HTMLElement | null) {
   return Array.from(
      list?.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])') ?? []
   );
}

function setActive(
   internals: SelectInternals,
   option: HTMLElement | null,
   { scroll = true }: { scroll?: boolean } = {}
) {
   if (internals.active) delete internals.active.dataset.highlighted;
   internals.active = option;
   if (!option) {
      internals.trigger?.removeAttribute('aria-activedescendant');
      return;
   }
   option.dataset.highlighted = '';
   internals.trigger?.setAttribute('aria-activedescendant', option.id);
   if (scroll) option.scrollIntoView?.({ block: 'nearest' });
}

// A trigger named through aria-labelledby would otherwise lend the list its
// content, the current value included, instead of its label.
function nameListAfter(list: HTMLElement, trigger: HTMLElement) {
   const label = trigger.getAttribute('aria-label');
   if (label) list.setAttribute('aria-label', label);
   else list.setAttribute('aria-labelledby', trigger.getAttribute('aria-labelledby') ?? trigger.id);
}

function moveActive(internals: SelectInternals, delta: number) {
   const options = enabledOptions(internals.list);
   if (options.length === 0) return;
   const current = internals.active ? options.indexOf(internals.active) : -1;
   const from = current === -1 ? (delta > 0 ? -1 : options.length) : current;
   const next = Math.min(Math.max(from + delta, 0), options.length - 1);
   setActive(internals, options[next]);
}

function highlightInitial(internals: SelectInternals) {
   if (internals.openIntent === 'search' && applySearch(internals)) return;
   const options = enabledOptions(internals.list);
   const selected = options.find(option => option.getAttribute('aria-selected') === 'true');
   if (internals.openIntent === 'first') setActive(internals, options[0] ?? null);
   else if (internals.openIntent === 'last') setActive(internals, options.at(-1) ?? null);
   else setActive(internals, selected ?? options[0] ?? null);
}

function pushSearch(internals: SelectInternals, char: string) {
   window.clearTimeout(internals.searchTimer);
   internals.search += char.toLowerCase();
   internals.searchTimer = window.setTimeout(() => {
      internals.search = '';
   }, SEARCH_RESET_MS);
}

function applySearch(internals: SelectInternals) {
   const options = enabledOptions(internals.list);
   const { search } = internals;
   // The same letter again steps through the options that start with it.
   const cycling = Array.from(search).every(char => char === search[0]);
   const query = cycling ? search[0] : search;
   const current = internals.active ? options.indexOf(internals.active) : -1;
   const from = cycling ? current + 1 : Math.max(current, 0);
   const match = [...options.slice(from), ...options.slice(0, from)].find(option =>
      (option.dataset.text ?? option.textContent ?? '').trim().toLowerCase().startsWith(query)
   );
   if (match) setActive(internals, match);
   return match != null;
}

export {
   Select,
   SelectContent,
   SelectGroup,
   SelectItem,
   SelectLabel,
   SelectSeparator,
   SelectTrigger,
   SelectValue,
};
